// cherry-pick lodash methods (the right way). DO NOT use import from 'lodash'.
import endsWith from 'lodash/endsWith';
import find from 'lodash/find';
import includes from 'lodash/includes';
import isString from 'lodash/isString';

import { Error } from './Error';
import { ErrorData } from './ErrorData';
import { ErrorType } from './ErrorType.enum';
import Utils from '../utils';

const { CommunicationStatus } = Utils;

export const ErrorService = {
	brand: {},

	errorData: {},

	genericErrorDescription: '',

	init(brand) {
		const { brandName, contactPhoneNumber } = brand || {};
		const learningCenter =
			brandName === 'Stamps.com'
				? 'https://www.stamps.com/help'
				: 'https://www.endicia.com/help';
		this.brand = { ...brand, brandName, contactPhoneNumber };
		this.errorData = ErrorData(this.brand);
		this.genericErrorDescription = `<p>For additional help, please visit our <a href=${learningCenter} target="_blank">Learning Center</a>.</p>`;
	},

	/**
	 * Create a new Error model instance based on the error data for
	 * displaying an error modal. When `null`, the error modal won't
	 * be displayed.
	 * @param {object} data
	 * 	- {string} handleType Custom logic for handling the error.
	 * 	- {number} code ErrorCode from ajax response.
	 * 	- {string} description ErrorDescription from ajax response.
	 *  - {CommunicationStatus} status
	 * @return {Error|null}
	 */
	process(data) {
		const { status, handleType, code, description, url } = data || {};

		// do not show the error when user isn't authorized
		if (status === CommunicationStatus.SignedOut) {
			return null;
		}

		// custom handling logic
		if (handleType === 'custom') {
			return new Error(data);
		} else {
			// handle all SWS/IM errors
			return this._handleSwsimError(code, description, url);
		}
	},

	/**
	 * Handle SWS/IM errors including GetRates errors [ErrorCode: 5177601].
	 * @param code ErrorCode from ajax response.
	 * @param description ErrorDescription from ajax response.
	 * @return {Error|null}
	 */
	_handleSwsimError(code, description, url) {
		// check if it's a GetRates error defined in ErrorData.js
		let data =
			code === 5177601
				? find(this.errorData.getRates, err => includes(err.searchCriteria, description))
				: null;

		// handle getRates errors defined in ErrorData.js
		if (data) {
			return new Error(data);
		}

		// else, check if it's a swsim error data defined in ErrorData.js
		data = find(this.errorData.swsim, { code });

		// handle swsim errors defined in ErrorData.js
		if (data) {
			if (data.type === ErrorType.Normal) {
				return new Error(data);
			} else if (data.type === ErrorType.Custom) {
				// check if the search criteria matches with the error description provided
				if (includes(description, data.searchCriteria)) {
					return new Error(data);
				} else {
					return this._buildGenericSwsimError(code, description);
				}
			} else {
				// suppress the message for the Silent-type errors
				return null;
			}
		}
		// handle errors that aren't defined in ErrorData.js and process the error description
		else if (includes(description, 'must specify a state for domestic addresses')) {
			return new Error({
				code,
				title: 'State Require',
				description:
					'<p>A state is required when shipping to a domestic address.</p><p>Please enter a Ship To state and try again.</p>',
			});
		}
		// handle errors that aren't defined in ErrorData.js and process the error description
		else if (includes(description, 'Communication failure')) {
			return new Error({
				code,
				title: 'Server Error',
				description: `<p>Communication failure.</p><p>${this.genericErrorDescription}</p>`,
			});
		} else if (includes(description, 'Payment Server rejected the payment.')) {
			const helpLink =
				this.brand.brandName === 'Stamps.com'
					? 'https://stamps.custhelp.com/app/answers/detail/a_id/5262'
					: 'https://endicia.custhelp.com/app/answers/detail/a_id/5262';

			return new Error({
				code,
				title: 'Unable to Add Funds',
				description: `<p>We're sorry, but there was an issue processing your payment. Review this <a href=${helpLink} target="_blank">Help Center article</a> to learn about common reasons for payment failures and steps to resolve them.</p>`,
			});
		} else if (includes(description, 'timeout of 30000ms')) {
			// handle simple server timeout error
			return new Error({
				code,
				title: 'Request Timeout',
				description: `<p>The request has timed out. Please try again.</p>
							<p>Additional information:</p>
							<p>Failed to connect to ${url}.</p>`,
			});
		}
		// build a generic swsim error object
		else {
			return this._buildGenericSwsimError(code, description);
		}
	},

	/**
	 * Create a new Error model instance for displaying the generic SWS/IM error.
	 * @param {number} code ErrorCode from ajax response.
	 * @param {string} description ErrorDescription from ajax response.
	 * @return {Error|null}
	 */

	_buildGenericSwsimError(code, description) {
		let errorDescription = '';

		// append a period (.) and some text to description
		if (isString(description) && description !== '') {
			errorDescription = [
				'<p>',
				`${description}`,
				endsWith(description, '.') ? '' : '.',
				' Please try again.',
				'</p>',
			].join('');
		}

		return new Error({
			code,
			description: `${errorDescription}<p>${this.genericErrorDescription}</p>`,
		});
	},
};
